/**
 * @file
 * Lazy editorial widgets: Swiper (galleries, partner slider), GLightbox.
 *
 * Drupal provides globals when this library loads (dependencies: core/drupal, core/once).
 */
import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import GLightbox from 'glightbox';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'glightbox/dist/css/glightbox.min.css';

/**
 * Partner showcase carousel — scoped navigation; breakpoints per spec.
 */
/**
 * Service slider carousel — 1 / 2 / 3 slides per breakpoint.
 */
/**
 * Shared teaser slider init (service + article).
 */
function zkInitTeaserSliderSwipers(context, selector, staticClass, a11yPrev, a11yNext) {
  const root = context && context.nodeType ? context : document;
  const nodes = root.querySelectorAll ? root.querySelectorAll(selector) : [];
  nodes.forEach((el) => {
    if (el.getAttribute('data-zk-teaser-swiper-done') === '1') {
      return;
    }
    el.setAttribute('data-zk-teaser-swiper-done', '1');
    const prevSel = el.getAttribute('data-slider-prev');
    const nextSel = el.getAttribute('data-slider-next');
    const prev = prevSel ? document.querySelector(prevSel) : null;
    const next = nextSel ? document.querySelector(nextSel) : null;
    const slides = el.querySelectorAll('.swiper-slide');
    if (slides.length < 2) {
      [prev, next].forEach((btn) => btn?.setAttribute('hidden', ''));
      el.classList.add(staticClass);
      return;
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // eslint-disable-next-line no-new
    new Swiper(el, {
      modules: [A11y, Navigation],
      grabCursor: !reducedMotion,
      watchOverflow: true,
      speed: reducedMotion ? 0 : 300,
      slidesPerView: 1,
      spaceBetween: 16,
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
      },
      navigation: {
        prevEl: prev || undefined,
        nextEl: next || undefined,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: a11yPrev,
        nextSlideMessage: a11yNext,
      },
    });
  });
}

function zkInitServiceSliderSwipers(context) {
  zkInitTeaserSliderSwipers(
    context,
    '.zk-service-slider__swiper[data-zk-service-swiper="1"]',
    'zk-service-slider__swiper--static',
    'Vorherige Services',
    'Nächste Services',
  );
}

function zkInitArticleSliderSwipers(context) {
  zkInitTeaserSliderSwipers(
    context,
    '.zk-article-slider__swiper[data-zk-article-swiper="1"]',
    'zk-article-slider__swiper--static',
    'Vorherige Beiträge',
    'Nächste Beiträge',
  );
}

function zkInitPartnerShowcaseSwipers(context) {
  const root = context && context.nodeType ? context : document;
  const nodes = root.querySelectorAll ? root.querySelectorAll('.zk-partner-showcase__swiper[data-zk-partner-swiper="1"]') : [];
  nodes.forEach((el) => {
    if (el.getAttribute('data-zk-partner-swiper-done') === '1') {
      return;
    }
    el.setAttribute('data-zk-partner-swiper-done', '1');
    const showcase = el.closest('.zk-partner-showcase');
    if (!showcase || showcase.getAttribute('data-display') !== 'slider') {
      return;
    }
    const prevSel = el.getAttribute('data-slider-prev');
    const nextSel = el.getAttribute('data-slider-next');
    const prev = prevSel ? document.querySelector(prevSel) : null;
    const next = nextSel ? document.querySelector(nextSel) : null;
    const slides = el.querySelectorAll('.swiper-slide');
    if (slides.length < 2) {
      [prev, next].forEach((btn) => btn?.setAttribute('hidden', ''));
      el.classList.add('zk-partner-showcase__swiper--static');
      return;
    }
    try {
      // eslint-disable-next-line no-new
      new Swiper(el, {
        modules: [A11y, Navigation],
        grabCursor: true,
        watchOverflow: true,
        slidesPerView: 1.1,
        spaceBetween: 16,
        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
          1440: {
            slidesPerView: 4,
            spaceBetween: 24,
          },
        },
        navigation: {
          prevEl: prev || undefined,
          nextEl: next || undefined,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Vorherige Folie',
          nextSlideMessage: 'Nächste Folie',
        },
      });
    }
    catch (e) {
      showcase.classList.add('zk-partner-showcase--fallback-grid');
      el.querySelector('.swiper-wrapper')?.classList.add('zk-partner-showcase__fallback-grid');
      [prev, next].forEach((btn) => btn?.setAttribute('hidden', ''));
    }
  });
}

function zkInitSwipers(context) {
  const roots = context.querySelectorAll('.zk-swiper.swiper');
  roots.forEach((el) => {
    if (el.dataset.zkSwiperInit === '1') {
      return;
    }
    el.dataset.zkSwiperInit = '1';
    const slideCount = el.querySelectorAll('.swiper-slide').length;
    if (slideCount < 2) {
      el.classList.add('zk-swiper--static');
      el.querySelectorAll('.zk-swiper__prev, .zk-swiper__next, .zk-swiper__pagination').forEach((ctl) => {
        ctl.setAttribute('hidden', '');
      });
      return;
    }
    const modules = [A11y, Navigation, Pagination];
    const loop = el.dataset.zkSwiperLoop === '1';
    const slidesPerView = Number.parseFloat(el.dataset.zkSwiperPerView || '1') || 1;
    const spaceBetween = Number.parseInt(el.dataset.zkSwiperSpace || '16', 10) || 16;
    // eslint-disable-next-line no-new
    new Swiper(el, {
      modules,
      loop,
      slidesPerView,
      spaceBetween,
      watchOverflow: true,
      watchSlidesProgress: true,
      keyboard: { enabled: true },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Vorherige Folie',
        nextSlideMessage: 'Nächste Folie',
      },
      pagination: {
        el: el.querySelector('.zk-swiper__pagination'),
        clickable: true,
      },
      navigation: {
        prevEl: el.querySelector('.zk-swiper__prev'),
        nextEl: el.querySelector('.zk-swiper__next'),
      },
    });
  });
}

function zkInitGlightbox(context) {
  const root = context && context.nodeType ? context : document;
  const scopes = root.querySelectorAll ? root.querySelectorAll('[data-zk-glightbox-scope]:not([data-zk-glb-done])') : [];
  scopes.forEach((scope) => {
    if (!scope.id) {
      return;
    }
    scope.setAttribute('data-zk-glb-done', '1');
    GLightbox({
      selector: `#${CSS.escape(scope.id)} a.zk-glightbox`,
      touchNavigation: true,
      loop: true,
      zoomable: false,
    });
  });
}

function zkEditorialAttach(context) {
  const root = context && context.nodeType ? context : document;
  zkInitPartnerShowcaseSwipers(root);
  zkInitServiceSliderSwipers(root);
  zkInitArticleSliderSwipers(root);
  zkInitSwipers(root);
  zkInitGlightbox(root);
}

if (typeof window !== 'undefined' && window.Drupal) {
  window.Drupal.behaviors.zkEditorialParagraphs = {
    attach(context) {
      zkEditorialAttach(context);
    },
  };
}
else {
  zkEditorialAttach(document);
}
